/**
 * Sitemap Configuration Dialog
 *
 * Allows users to configure and test custom sitemap URLs
 * when the default sitemap detection fails.
 *
 * Part of AEO Priorities - Sitemap Configuration UI
 *
 * Date: January 20, 2026
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, CheckCircle2, AlertCircle, Globe, FileText, Settings } from 'lucide-react';
import { toast } from '@/components/ui/use-toast';
import {
  testSitemap,
  saveSitemapUrl,
  getSitemapUrlConfig,
  SitemapTestResult,
} from '@/services/aeoPrioritiesService';

interface SitemapConfigDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  siteId: string;
  onSuccess: () => void;
}

export function SitemapConfigDialog({
  open,
  onOpenChange,
  siteId,
  onSuccess,
}: SitemapConfigDialogProps) {
  const [sitemapUrl, setSitemapUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<SitemapTestResult | null>(null);
  const [defaultUrl, setDefaultUrl] = useState<string | null>(null);

  // Load current config when dialog opens
  useEffect(() => {
    if (open && siteId) {
      loadCurrentConfig();
    }
  }, [open, siteId]);

  const loadCurrentConfig = async () => {
    setLoading(true);
    setError(null);
    setTestResult(null);
    try {
      const config = await getSitemapUrlConfig(siteId);
      setSitemapUrl(config.sitemapUrl || config.defaultUrl || '');
      setDefaultUrl(config.defaultUrl);
    } catch (err) {
      console.error('Failed to load sitemap config:', err);
    } finally {
      setLoading(false);
    }
  };

  const validateUrl = (urlString: string): boolean => {
    try {
      const url = new URL(urlString);
      if (!['http:', 'https:'].includes(url.protocol)) {
        setError('URL must use http or https protocol');
        return false;
      }
      return true;
    } catch {
      setError('Please enter a valid URL');
      return false;
    }
  };

  const handleTest = async () => {
    setError(null);
    setTestResult(null);

    if (!validateUrl(sitemapUrl)) {
      return;
    }

    setTesting(true);
    try {
      const result = await testSitemap(siteId, sitemapUrl);
      setTestResult(result);

      if (!result.success) {
        const errorMsg = result.errors?.[0]?.message || 'Sitemap test failed';
        setError(errorMsg);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to test sitemap');
    } finally {
      setTesting(false);
    }
  };

  const handleSaveAndCalculate = async () => {
    if (!testResult?.success) {
      setError('Please test the sitemap URL first');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      await saveSitemapUrl(siteId, sitemapUrl);

      toast({
        title: 'Sitemap URL Saved',
        description: `Configured sitemap: ${sitemapUrl} with ${testResult.urlsFound.toLocaleString()} URLs`,
      });

      onOpenChange(false);
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save sitemap URL');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setSitemapUrl('');
    setError(null);
    setTestResult(null);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[550px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Configure Sitemap URL
          </DialogTitle>
          <DialogDescription>
            Enter your sitemap URL to enable AEO priority calculations. The sitemap helps identify
            which pages are most important for AI visibility optimization.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          {/* URL Input */}
          <div className="grid gap-2">
            <Label htmlFor="sitemap-url">Sitemap URL</Label>
            <div className="flex gap-2">
              <Input
                id="sitemap-url"
                type="url"
                placeholder="https://example.com/sitemap.xml"
                value={sitemapUrl}
                onChange={(e) => {
                  setSitemapUrl(e.target.value);
                  setError(null);
                  setTestResult(null);
                }}
                disabled={loading || testing || saving}
                className="flex-1"
              />
              <Button
                type="button"
                variant="outline"
                onClick={handleTest}
                disabled={loading || testing || saving || !sitemapUrl}
              >
                {testing ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Globe className="h-4 w-4" />
                )}
                <span className="ml-2">Test</span>
              </Button>
            </div>
            {defaultUrl && (
              <p className="text-sm text-muted-foreground">
                Default: {defaultUrl}
              </p>
            )}
          </div>

          {/* Test Result */}
          {testResult && (
            <Alert variant={testResult.success ? 'default' : 'destructive'}>
              {testResult.success ? (
                <CheckCircle2 className="h-4 w-4 text-green-600" />
              ) : (
                <AlertCircle className="h-4 w-4" />
              )}
              <AlertDescription>
                {testResult.success ? (
                  <div className="flex flex-col gap-1">
                    <span className="font-medium text-green-600">
                      Success! Found {testResult.urlsFound.toLocaleString()} URLs
                    </span>
                    <span className="text-sm text-muted-foreground">
                      HTTP {testResult.httpStatus} - {testResult.contentType}
                    </span>
                    {/* Show warnings if URLs were extracted but with issues */}
                    {testResult.errors?.filter(e => e.type === 'xml_warning').map((warning, i) => (
                      <span key={i} className="text-sm text-amber-600">
                        Note: {warning.message}
                      </span>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col gap-1">
                    <span className="font-medium">Sitemap test failed</span>
                    {testResult.errors?.map((err, i) => (
                      <span key={i} className="text-sm">{err.message}</span>
                    ))}
                  </div>
                )}
              </AlertDescription>
            </Alert>
          )}

          {/* Error Message */}
          {error && !testResult && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Hints */}
          <div className="rounded-lg bg-muted p-3 text-sm">
            <div className="flex items-start gap-2">
              <FileText className="h-4 w-4 mt-0.5 text-muted-foreground" />
              <div className="text-muted-foreground">
                <p className="font-medium text-foreground">Sitemap Tips:</p>
                <ul className="list-disc list-inside mt-1 space-y-1">
                  <li>Check your robots.txt for the sitemap location</li>
                  <li>Common paths: /sitemap.xml, /sitemap_index.xml</li>
                  <li>For subdomains, use the full domain (e.g., www.example.com)</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveAndCalculate}
            disabled={!testResult?.success || saving}
          >
            {saving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <CheckCircle2 className="mr-2 h-4 w-4" />
                Save & Calculate Priorities
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
